import React, { ReactNode } from "react";
import {
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import StackChip from "./StackChip";

export type ProjectCardProps = {
  title: string;
  client: string;
  color: string; // signature color — drives card accent AND scene tint
  bullets: string[];
  stack: string[];
  size: "deck" | "panel"; // deck = scenes 2–4, panel = scene 5
  enterFrame: number; // when the card animates in
  flipFrame?: number; // when the card flips to reveal the stack; omit = no flip
  icon?: ReactNode;
  children?: ReactNode; // extra front-face content (e.g. embedded gif)
};

const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  client,
  color,
  bullets,
  stack,
  size,
  enterFrame,
  flipFrame,
  icon,
  children,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const enter = spring({
    frame: frame - enterFrame,
    fps,
    config: { damping: 16, stiffness: 120, mass: 0.9 },
  });

  const flip =
    flipFrame === undefined
      ? 0
      : spring({
          frame: frame - flipFrame,
          fps,
          config: { damping: 18, stiffness: 90 },
        });

  const width = size === "deck" ? 600 : 1500;
  const minHeight = size === "deck" ? 620 : 780;

  const face: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    borderRadius: 28,
    border: `3px solid ${color}`,
    backgroundColor: "rgba(24, 24, 24, 0.92)",
    boxShadow: `0 0 60px ${color}44, inset 0 0 120px rgba(0,0,0,0.6)`,
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    padding: size === "deck" ? "36px 40px" : "48px 64px",
    textAlign: "left",
  };

  return (
    <div style={{ perspective: 2000, width, minHeight, position: "relative" }}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          transformStyle: "preserve-3d",
          opacity: interpolate(enter, [0, 0.4], [0, 1], {
            extrapolateRight: "clamp",
          }),
          translate: `0px ${(1 - enter) * 160}px`,
          rotate: `y ${flip * 180}deg`,
        }}
      >
        {/* Front face */}
        <div style={face}>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 10,
              backgroundColor: color,
            }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 16,
              marginTop: 8,
            }}
          >
            {icon ? (
              <div style={{ color, fontSize: 0 }}>{icon}</div>
            ) : null}
            <div>
              <div
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  fontSize: size === "deck" ? 38 : 54,
                  color: "white",
                  lineHeight: 1.2,
                }}
              >
                {title}
              </div>
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: size === "deck" ? 22 : 28,
                  color,
                  marginTop: 6,
                }}
              >
                {client}
              </div>
            </div>
          </div>
          <ul style={{ paddingLeft: 28, marginTop: 28, flex: 1 }}>
            {bullets.map((bullet, i) => (
              <li
                key={bullet}
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: size === "deck" ? 24 : 30,
                  lineHeight: 1.55,
                  color: "#d6d6d6",
                  marginBottom: 14,
                  opacity: interpolate(
                    frame,
                    [enterFrame + 6 + i * 5, enterFrame + 14 + i * 5],
                    [0, 1],
                    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                  ),
                  translate: `${interpolate(
                    frame,
                    [enterFrame + 6 + i * 5, enterFrame + 14 + i * 5],
                    [24, 0],
                    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                  )}px 0px`,
                }}
              >
                {bullet}
              </li>
            ))}
          </ul>
          {children}
        </div>
        {/* Back face — stack reveal */}
        <div
          style={{
            ...face,
            rotate: "y 180deg",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 26,
              color,
              marginBottom: 24,
              letterSpacing: 4,
            }}
          >
            STACK
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "center",
              maxWidth: width - 80,
            }}
          >
            {stack.map((tech, i) => (
              <StackChip
                key={tech}
                text={tech}
                color={color}
                fontSize={size === "deck" ? 24 : 28}
                enterFrame={
                  flipFrame === undefined ? undefined : flipFrame + 6 + i * 3
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
